import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { config } from "@/config";
import { apiFetch } from "@/lib/api-client";
import { toast } from "sonner";
import {
  GRAPH_DB_KIND_TITLE,
  GRAPH_DB_LABEL,
  GRAPH_DB_SLUG,
  toProperties,
  type GraphDbKind,
  type GraphDbProperty,
} from "@/lib/graph-db-schema";
import {
  normalizeListResponse,
  parseJson,
  throwOnError,
  unwrap,
  withProjectUuid,
} from "@/service-hooks/use-get-nosql-ontology-service";

// ── Types ──────────────────────────────────────────────────────────────
// Graph-database schema: GraphNodeType, GraphEdgeType and GraphIndex, side by
// side under a DataLake (flat — nothing links them; an edge type names its two
// node types as plain text). Routes live under the common /db-ontology.

export interface GraphDbItem {
  id?: string;
  name: string;
  description?: string | null;
  /** Node and edge types: the property list. */
  properties: GraphDbProperty[];
  /** Edge types: the node type names it joins (plain text). */
  from?: string;
  to?: string;
  /** Indexes: what kind, and the database's own definition text. */
  kind?: string;
  definition?: string;
  dbOntologyId?: string;
  projectUuid?: string;
}

export interface GraphDbList {
  items: GraphDbItem[];
  total: number;
}

const normalizeItem = (raw: any): GraphDbItem => ({
  ...(raw?.id ? { id: String(raw.id) } : {}),
  name: String(raw?.name ?? ""),
  description: raw?.description ?? null,
  properties: toProperties(raw?.properties),
  from: raw?.from ?? undefined,
  to: raw?.to ?? undefined,
  kind: raw?.kind ?? undefined,
  definition: raw?.definition ?? undefined,
  dbOntologyId: raw?.dbOntologyId ?? undefined,
  projectUuid: raw?.projectUuid ?? undefined,
});

const base = () => `${config.isometricApiUrl}/db-ontology`;
const queryKey = (kind: GraphDbKind, dataLakeId?: string) => ["graph-db-ontology", kind, dataLakeId] as const;

// ── Queries ────────────────────────────────────────────────────────────

export function useGetGraphDbItems(
  kind: GraphDbKind,
  dataLakeId: string | undefined,
  projectUuid: string | undefined,
  enabled: boolean = true,
) {
  const { getAuthHeaders } = useAuthHeaders();
  return useQuery({
    queryKey: [...queryKey(kind, dataLakeId), projectUuid ?? null],
    queryFn: async (): Promise<GraphDbList> => {
      const url = withProjectUuid(
        `${base()}/${encodeURIComponent(dataLakeId!)}/${GRAPH_DB_LABEL[kind]}?limit=500`,
        projectUuid!,
      );
      const response = await apiFetch(url, { method: "GET", headers: getAuthHeaders() });
      await throwOnError(response, `Failed to fetch ${GRAPH_DB_KIND_TITLE[kind].toLowerCase()}`);
      const payload = await parseJson(response);
      const items = normalizeListResponse(payload, normalizeItem);
      const total = typeof payload?.total === "number" ? payload.total : items.length;
      return { items, total };
    },
    enabled: enabled && !!dataLakeId && !!projectUuid,
    staleTime: 60000,
    retry: 1,
  });
}

// ── Mutations ──────────────────────────────────────────────────────────

/** A save can succeed with warnings (e.g. an edge type now names a node type that is gone). Show them, don't block. */
const warn = (result: unknown) => {
  const warnings = (unwrap(result) as any)?.warnings;
  if (Array.isArray(warnings)) for (const w of warnings) toast.warning(String(w));
};

export interface CreateGraphDbPayload {
  kind: GraphDbKind;
  projectUuid: string;
  dataLakeId: string;
  data: Record<string, unknown>;
}

export function useCreateGraphDbItem() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ kind, projectUuid, dataLakeId, data }: CreateGraphDbPayload) => {
      const url = withProjectUuid(`${base()}/${GRAPH_DB_SLUG[kind]}`, projectUuid);
      const response = await apiFetch(url, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ ...data, projectUuid, dataLakeId }),
      });
      await throwOnError(response, "Failed to save");
      return { status: response.status, body: await parseJson(response) };
    },
    onSuccess: ({ status, body }, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKey(variables.kind, variables.dataLakeId) });
      // 200 (not 201) means that name already existed and was left as it was.
      if (status === 200) toast.info("Already exists — nothing changed");
      else toast.success("Created");
      warn(body);
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to save");
    },
  });
}

export interface UpdateGraphDbPayload {
  kind: GraphDbKind;
  id: string;
  projectUuid: string;
  dataLakeId: string;
  /** For a node or edge type, the COMPLETE property list — the backend replaces it, never merges. */
  data: Record<string, unknown>;
}

export function useUpdateGraphDbItem() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ kind, id, projectUuid, data }: UpdateGraphDbPayload) => {
      const url = withProjectUuid(`${base()}/${GRAPH_DB_SLUG[kind]}/${encodeURIComponent(id)}`, projectUuid);
      const response = await apiFetch(url, { method: "PUT", headers: getAuthHeaders(), body: JSON.stringify(data) });
      await throwOnError(response, "Failed to update");
      return parseJson(response);
    },
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKey(variables.kind, variables.dataLakeId) });
      toast.success("Updated");
      warn(result);
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update");
    },
  });
}

export interface DeleteGraphDbPayload {
  kind: GraphDbKind;
  id: string;
  projectUuid: string;
  dataLakeId: string;
}

export function useDeleteGraphDbItem() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ kind, id, projectUuid }: DeleteGraphDbPayload) => {
      const url = withProjectUuid(`${base()}/${GRAPH_DB_SLUG[kind]}/${encodeURIComponent(id)}`, projectUuid);
      const response = await apiFetch(url, { method: "DELETE", headers: getAuthHeaders() });
      await throwOnError(response, "Failed to delete");
      return parseJson(response);
    },
    onSuccess: (result, variables) => {
      // Edge types and indexes name node types as text only, so deleting a node type touches no other row;
      // the backend's warnings say which edge types now point at nothing.
      queryClient.invalidateQueries({ queryKey: queryKey(variables.kind, variables.dataLakeId) });
      toast.success("Deleted");
      warn(result);
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete");
    },
  });
}
